
import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, Trash2, Info, MapPin } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const projectSchema = z.object({
  projectName: z.string().min(2, {
    message: "Project name must be at least 2 characters.",
  }),
  tenements: z.array(
    z.object({
      name: z.string().min(2, { message: "Tenement name must be at least 2 characters." }),
      permitNumber: z.string().min(2, { message: "Permit number must be at least 2 characters." }),
      location: z.string().min(2, { message: "Location must be at least 2 characters." }),
      size: z.coerce.number().min(0, { message: "Size must be a positive number." }),
      status: z.string().min(1, { message: "Please select a status." }),
      acquisitionDate: z.string().min(1, { message: "Please provide an acquisition date." }),
      expiryDate: z.string().min(1, { message: "Please provide an expiry date." }),
      ownershipPercentage: z.coerce.number().min(0).max(100, {
        message: "Ownership percentage must be between a 0 and 100."
      }),
    })
  ).min(1, { message: "At least one tenement is required" }),
  mineralAssets: z.array(
    z.object({
      name: z.string().min(1, { message: "Asset name is required" }),
      mineralType: z.string().min(1, { message: "Mineral type is required" }),
      status: z.string().min(1, { message: "Status is required" }),
      confidence: z.string().min(1, { message: "Confidence level is required" }),
      estimatedResource: z.coerce.number().optional(),
      estimatedGrade: z.coerce.number().optional(),
      gradeUnit: z.string().optional(),
      discountFactor: z.coerce.number().min(0).max(100, { 
        message: "Discount factor must be between 0 and 100" 
      }),
    })
  ).min(1, { message: "At least one mineral asset is required" }),
});

const financialSchema = z.object({
  mineralPrice: z.coerce.number().min(0, {
    message: "Price must be a positive number.",
  }),
  discountFactor: z.coerce.number().min(0).max(100, {
    message: "Discount factor must be between 0 and 100.",
  }),
  processingCost: z.coerce.number().min(0, {
    message: "Cost must be a positive number.",
  }),
  transportCost: z.coerce.number().min(0, {
    message: "Cost must be a positive number.",
  }),
  royaltyRate: z.coerce.number().min(0).max(100, {
    message: "Royalty rate must be between 0 and 100.",
  }),
  taxRate: z.coerce.number().min(0).max(100, {
    message: "Tax rate must be between 0 and 100.",
  }),
  discountRate: z.coerce.number().min(0).max(100, {
    message: "Discount rate must be between 0 and 100.",
  }),
});

const riskSchema = z.object({
  priceVolatility: z.coerce.number().min(0).max(100, {
    message: "Volatility must be between 0 and 100.",
  }),
  recoveryRate: z.coerce.number().min(0).max(100, {
    message: "Recovery rate must be between 0 and 100.",
  }),
  geopoliticalRisk: z.string().min(1, {
    message: "Please select a risk level.",
  }),
  environmentalRisk: z.string().min(1, {
    message: "Please select a risk level.",
  }),
});

export function DataInputForm() {
  const projectForm = useForm<z.infer<typeof projectSchema>>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      projectName: "",
      tenements: [
        {
          name: "",
          permitNumber: "",
          location: "",
          size: 0,
          status: "",
          acquisitionDate: "",
          expiryDate: "",
          ownershipPercentage: 100,
        }
      ],
      mineralAssets: [
        {
          name: "",
          mineralType: "",
          status: "exploration",
          confidence: "medium",
          estimatedResource: 0,
          estimatedGrade: 0,
          gradeUnit: "",
          discountFactor: 30,
        }
      ],
    },
  });

  const { fields: tenementsFields, append: appendTenement, remove: removeTenement } = useFieldArray({
    control: projectForm.control,
    name: "tenements",
  });

  const { fields: mineralAssetsFields, append: appendMineralAsset, remove: removeMineralAsset } = useFieldArray({
    control: projectForm.control,
    name: "mineralAssets",
  });

  const financialForm = useForm<z.infer<typeof financialSchema>>({
    resolver: zodResolver(financialSchema),
    defaultValues: {
      mineralPrice: 0,
      discountFactor: 30, // Initial discount factor for early stage
      processingCost: 0,
      transportCost: 0,
      royaltyRate: 0,
      taxRate: 0,
      discountRate: 10,
    },
  });

  const riskForm = useForm<z.infer<typeof riskSchema>>({
    resolver: zodResolver(riskSchema),
    defaultValues: {
      priceVolatility: 0,
      recoveryRate: 85,
      geopoliticalRisk: "",
      environmentalRisk: "",
    },
  });

  function onProjectSubmit(values: z.infer<typeof projectSchema>) {
    console.log(values);
    // Would save this data to a store or context in a real application
  }

  function onFinancialSubmit(values: z.infer<typeof financialSchema>) {
    console.log(values);
    // Would save this data to a store or context in a real application
  }

  function onRiskSubmit(values: z.infer<typeof riskSchema>) {
    console.log(values);
    // Would save this data to a store or context in a real application
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "prospecting":
        return <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">Prospecting</Badge>;
      case "exploration":
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">Exploration</Badge>;
      case "development":
        return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">Development</Badge>;
      case "production":
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">Production</Badge>;
      case "dormant":
        return <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-300">Dormant</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-0.5">
        <h2 className="text-2xl font-bold tracking-tight">Project Data Input</h2>
        <p className="text-muted-foreground">
          Enter project details for accurate financial forecasting.
        </p>
      </div>
      
      <Tabs defaultValue="project">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="project">Tenements & Minerals</TabsTrigger>
          <TabsTrigger value="financial">Financial Parameters</TabsTrigger>
          <TabsTrigger value="risk">Risk Factors</TabsTrigger>
        </TabsList>
        
        <TabsContent value="project" className="animate-slide-in">
          <Card>
            <CardHeader>
              <CardTitle>Tenements & Mineral Assets</CardTitle>
              <CardDescription>
                Enter information about your tenements and their mineral assets.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...projectForm}>
                <form onSubmit={projectForm.handleSubmit(onProjectSubmit)} className="space-y-6">
                  <FormField
                    control={projectForm.control}
                    name="projectName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Gold Ridge Exploration" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Separator className="my-4" />
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Tenements</h3>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">Add multiple tenements (mining permits or exploration licenses). Each tenement can have different locations and characteristics.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add all tenements associated with this project
                    </p>
                  </div>
                  
                  {tenementsFields.map((tenementField, tenementIndex) => (
                    <div key={tenementField.id} className="p-4 border rounded-md space-y-4 bg-muted/30">
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium flex items-center">
                          <MapPin className="h-4 w-4 mr-1" />
                          Tenement {tenementIndex + 1}
                        </h4>
                        {tenementsFields.length > 1 && (
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={() => removeTenement(tenementIndex)}
                            type="button"
                          >
                            <Trash2 className="h-4 w-4 mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={projectForm.control}
                          name={`tenements.${tenementIndex}.name`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tenement Name</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. Gold Ridge" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={projectForm.control}
                          name={`tenements.${tenementIndex}.permitNumber`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Permit Number</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. EP123456" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={projectForm.control}
                          name={`tenements.${tenementIndex}.location`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Location</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. Otago, New Zealand" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={projectForm.control}
                          name={`tenements.${tenementIndex}.size`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Size (hectares)</FormLabel>
                              <FormControl>
                                <Input type="number" placeholder="0" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={projectForm.control}
                          name={`tenements.${tenementIndex}.status`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tenement Status</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select status" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="prospecting">Prospecting</SelectItem>
                                  <SelectItem value="exploration">Exploration</SelectItem>
                                  <SelectItem value="development">Development</SelectItem>
                                  <SelectItem value="production">Production</SelectItem>
                                  <SelectItem value="dormant">Dormant</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={projectForm.control}
                          name={`tenements.${tenementIndex}.acquisitionDate`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Acquisition Date</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={projectForm.control}
                          name={`tenements.${tenementIndex}.expiryDate`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Expiry Date</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <FormField
                        control={projectForm.control}
                        name={`tenements.${tenementIndex}.ownershipPercentage`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Ownership Percentage (%)</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="100" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => appendTenement({
                      name: "",
                      permitNumber: "",
                      location: "",
                      size: 0,
                      status: "",
                      acquisitionDate: "",
                      expiryDate: "",
                      ownershipPercentage: 100,
                    })}
                    className="mt-2"
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Add Another Tenement
                  </Button>
                  
                  <Separator className="my-4" />
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Mineral Assets</h3>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">Add multiple mineral assets within these tenements. Each asset can have different minerals and valuation parameters.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add all mineral assets found within the tenements
                    </p>
                  </div>
                  
                  {mineralAssetsFields.map((field, index) => (
                    <div key={field.id} className="p-4 border rounded-md space-y-4 bg-muted/30">
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium">Mineral Asset {index + 1}</h4>
                        {mineralAssetsFields.length > 1 && (
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={() => removeMineralAsset(index)}
                            type="button"
                          >
                            <Trash2 className="h-4 w-4 mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={projectForm.control}
                          name={`mineralAssets.${index}.name`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Asset Name</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. North Vein Gold" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={projectForm.control}
                          name={`mineralAssets.${index}.mineralType`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Mineral Type</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select mineral type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="gold">Gold</SelectItem>
                                  <SelectItem value="silver">Silver</SelectItem>
                                  <SelectItem value="copper">Copper</SelectItem>
                                  <SelectItem value="iron">Iron</SelectItem>
                                  <SelectItem value="lithium">Lithium</SelectItem>
                                  <SelectItem value="rare_earth">Rare Earth Elements</SelectItem>
                                  <SelectItem value="uranium">Uranium</SelectItem>
                                  <SelectItem value="zinc">Zinc</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={projectForm.control}
                          name={`mineralAssets.${index}.status`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Exploration Status</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select status" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="prospecting">Prospecting</SelectItem>
                                  <SelectItem value="exploration">Exploration</SelectItem>
                                  <SelectItem value="development">Development</SelectItem>
                                  <SelectItem value="production">Production</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={projectForm.control}
                          name={`mineralAssets.${index}.confidence`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Confidence Level</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select confidence" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="low">
                                    <div className="flex items-center">
                                      <span>Low</span>
                                      <Badge className="ml-2 bg-yellow-500">Low</Badge>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="medium">
                                    <div className="flex items-center">
                                      <span>Medium</span>
                                      <Badge className="ml-2 bg-blue-500">Medium</Badge>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="high">
                                    <div className="flex items-center">
                                      <span>High</span>
                                      <Badge className="ml-2 bg-green-500">High</Badge>
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={projectForm.control}
                          name={`mineralAssets.${index}.estimatedResource`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Estimated Resource (tonnes)</FormLabel>
                              <FormControl>
                                <Input type="number" placeholder="0" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={projectForm.control}
                          name={`mineralAssets.${index}.estimatedGrade`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Estimated Grade</FormLabel>
                              <FormControl>
                                <Input type="number" placeholder="0" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={projectForm.control}
                          name={`mineralAssets.${index}.gradeUnit`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Grade Unit</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select unit" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="g/t">g/t (grams per tonne)</SelectItem>
                                  <SelectItem value="%">% (percentage)</SelectItem>
                                  <SelectItem value="oz/t">oz/t (ounces per ton)</SelectItem>
                                  <SelectItem value="ppm">ppm (parts per million)</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <FormField
                        control={projectForm.control}
                        name={`mineralAssets.${index}.discountFactor`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              Discount Factor (%)
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Info className="h-4 w-4 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-xs">Applied to early-stage projects to account for geological, technical, and economic uncertainty. Higher values indicate greater risk.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="30" {...field} />
                            </FormControl>
                            <FormDescription>
                              Higher discount for earlier stage projects (30-90%)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => appendMineralAsset({
                      name: "",
                      mineralType: "",
                      status: "exploration",
                      confidence: "medium",
                      estimatedResource: 0,
                      estimatedGrade: 0,
                      gradeUnit: "",
                      discountFactor: 30,
                    })}
                    className="mt-2"
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Add Another Mineral Asset
                  </Button>
                  
                  <Button type="submit" className="mt-6">Save Project Details</Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="financial" className="animate-slide-in">
          <Card>
            <CardHeader>
              <CardTitle>Financial Parameters</CardTitle>
              <CardDescription>
                Enter pricing and valuation parameters for your financial model.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...financialForm}>
                <form onSubmit={financialForm.handleSubmit(onFinancialSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={financialForm.control}
                      name="mineralPrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mineral Price ($/unit)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="0" {...field} />
                          </FormControl>
                          <FormDescription>
                            Current market price per unit
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={financialForm.control}
                      name="discountFactor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            Discount Factor (%)
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Info className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">Reduces the estimated value based on exploration stage, geological certainty, and technical risk. Higher values for earlier stage projects.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="30" {...field} />
                          </FormControl>
                          <FormDescription>
                            Default discount for early-stage exploration (30-90%)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={financialForm.control}
                      name="processingCost"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Processing Cost ($/unit)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="0" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={financialForm.control}
                      name="transportCost"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Transport Cost ($/unit)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="0" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                      control={financialForm.control}
                      name="royaltyRate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Royalty Rate (%)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="0" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={financialForm.control}
                      name="taxRate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tax Rate (%)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="0" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={financialForm.control}
                      name="discountRate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Discount Rate (%)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="10" {...field} />
                          </FormControl>
                          <FormDescription>
                            For NPV calculations
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <Button type="submit">Save Financial Parameters</Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="risk" className="animate-slide-in">
          <Card>
            <CardHeader>
              <CardTitle>Risk Factors</CardTitle>
              <CardDescription>
                Assess various risk factors that might impact your project.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...riskForm}>
                <form onSubmit={riskForm.handleSubmit(onRiskSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={riskForm.control}
                      name="priceVolatility"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Price Volatility (%)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="0" {...field} />
                          </FormControl>
                          <FormDescription>
                            Expected annual price fluctuation
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={riskForm.control}
                      name="recoveryRate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Recovery Rate (%)</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="85" {...field} />
                          </FormControl>
                          <FormDescription>
                            Percentage of mineral recoverable from ore
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={riskForm.control}
                      name="geopoliticalRisk"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Geopolitical Risk</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select risk level" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="low">
                                <div className="flex items-center">
                                  <span>Low</span>
                                  <Badge className="ml-2 bg-green-500">Low</Badge>
                                </div>
                              </SelectItem>
                              <SelectItem value="medium">
                                <div className="flex items-center">
                                  <span>Medium</span>
                                  <Badge className="ml-2 bg-yellow-500">Medium</Badge>
                                </div>
                              </SelectItem>
                              <SelectItem value="high">
                                <div className="flex items-center">
                                  <span>High</span>
                                  <Badge className="ml-2 bg-red-500">High</Badge>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Political stability of project location
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={riskForm.control}
                      name="environmentalRisk"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Environmental Risk</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select risk level" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="low">
                                <div className="flex items-center">
                                  <span>Low</span>
                                  <Badge className="ml-2 bg-green-500">Low</Badge>
                                </div>
                              </SelectItem>
                              <SelectItem value="medium">
                                <div className="flex items-center">
                                  <span>Medium</span>
                                  <Badge className="ml-2 bg-yellow-500">Medium</Badge>
                                </div>
                              </SelectItem>
                              <SelectItem value="high">
                                <div className="flex items-center">
                                  <span>High</span>
                                  <Badge className="ml-2 bg-red-500">High</Badge>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Environmental sensitivity and regulatory requirements
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <Button type="submit">Save Risk Assessment</Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
